/**
 * Per-principal field validation message helpers.
 *
 * The bond-creation forms (Guardianship / Probate / Trustee / VA) all share
 * the same per-principal field naming under the hood (`guardians.<index>.<field>`),
 * but used to surface a single useless toast - "Please fill all required
 * principal fields" - whenever ANY of those fields was missing. That hid:
 *
 *   - which principal was incomplete (#1, #2, ...)
 *   - which field was missing (Street Address, City, ...)
 *   - the special case where a Professional Guardian was selected from the
 *     dropdown but their address blob in the DB is malformed and the backend
 *     could not parse it into 4 clean components (the Kathleen-Hayes case)
 *
 * This helper turns the validator's `errors` object into a small, ordered
 * list of human-readable messages so the toast can finally tell the user
 * exactly what to do.
 */

// Maps the internal field names to the labels that actually appear under
// the input on screen so the toast reads naturally. Both naming conventions
// used across the 4 bond-creation forms are listed here:
//   - GuardianshipBond.jsx uses  `guardian_name` / `guardian_street_address_1`
//     / `guardian_zip` / `guardian_city` / `guardian_state`
//   - ProbateBond / TrusteeBond / VABond use  `name` / `address` / `zip_code`
//     / `city` / `state`
const FIELD_LABELS = {
  // Guardianship naming
  guardian_name: "Full Name",
  guardian_street_address_1: "Street Address",
  guardian_zip: "ZIP/Postal Code",
  guardian_city: "City",
  guardian_state: "State",
  guardian_relationship_to_ward: "Relationship to Ward",
  guardian_citizenship: "Citizenship",
  guardian_occupation: "Employment Status / Occupation",
  // Probate / Trustee / VA naming
  name: "Full Name",
  address: "Street Address",
  zip_code: "ZIP/Postal Code",
  city: "City",
  state: "State",
  relationship_to_ward: "Relationship to Ward",
  citizenship: "Citizenship",
  occupation: "Employment Status / Occupation",
  // Shared
  is_esignatures_principal: "Will you prefer to e-sign the Indemnity Agreement?",
  credit_report_principal: "Authorization to obtain underwriting information",
  email: "Email",
  ssn: "SSN",
  country: "Country",
};

/**
 * Build per-principal error messages from a flat `errors` map keyed by
 * `guardians.<index>.<field>`.
 *
 * @param {Object<string,string>} errors - validator output
 * @returns {string[]} ordered list of one-line messages, ready for
 *   `toast.error(msg)` / `toast.warning(msg)`. Returns [] when no
 *   principal-related errors exist.
 */
export function buildPrincipalErrorMessages(errors) {
  if (!errors) return [];

  const perPrincipal = new Map(); // index -> string[]

  Object.entries(errors).forEach(([key, message]) => {
    if (!key.startsWith("guardians.")) return;
    const parts = key.split(".");
    if (parts.length < 3) return;
    const index = Number(parts[1]);
    const field = parts[2];

    const label = FIELD_LABELS[field] || field.replace(/_/g, " ");
    const detail =
      message && !/^this field is required$/i.test(message)
        ? message
        : `${label} is required`;

    if (!perPrincipal.has(index)) perPrincipal.set(index, []);
    perPrincipal.get(index).push(detail);
  });

  // Sort by principal index and produce one toast line per principal.
  return Array.from(perPrincipal.keys())
    .sort((a, b) => a - b)
    .map((index) => {
      const messages = perPrincipal.get(index);
      return `Principal #${index + 1}: ${messages.join(", ")}`;
    });
}

/**
 * Convenience wrapper: when there are non-principal errors that the existing
 * fallback toast already handles, this returns ``null`` so callers can fall
 * through. When there ARE principal errors, it returns the joined message
 * (one toast per principal).
 */
export function getPrincipalToastMessages(errors) {
  const messages = buildPrincipalErrorMessages(errors);
  return messages.length > 0 ? messages : null;
}
